<template>
  <div class="permissions-matrix">
    <div
      class="d-flex justify-content-between align-items-center mb-1 mt-3"
      style="font-size: 16px"
    >
      <p class="mb-0" style="font-weight: 1000; color: black">
        User Permissions Matrix
      </p>
      <div>
        <b-button
          variant="success"
          size="sm"
          class="d-flex align-items-center"
          style="height: 38px"
          @click="handleShowModal"
        >
          <feather-icon
            icon="PlusIcon"
            size="16"
            class="align-middle text-white mr-1"
          />
          Add New User
        </b-button>
      </div>
    </div>

    <div class="table-responsive">
      <table class="table table-bordered">
        <thead class="table-light">
          <tr>
            <th class="user-column">User / Resource Type</th>
            <th
              v-for="resource in resources"
              :key="resource.key"
              class="resource-header"
            >
              <div class="resource-title">{{ resource.name }}</div>
            </th>
            <th class="actions-column">ACTIONS</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="admin in adminList" :key="admin.id">
            <td class="user-info">
              <div class="d-flex align-items-center">
                <div class="user-avatar me-2">
                  <i class="fas fa-user-circle text-muted"></i>
                </div>
                <div>
                  <div class="user-name">
                    {{
                      `${admin.firstName}${
                        admin.middleName ? ` ${admin.middleName} ` : " "
                      }${admin.lastName}`
                    }}
                  </div>
                  <div v-if="admin.roles.length > 0" class="user-role">
                    {{ admin.roles[0].name }}
                  </div>
                </div>
              </div>
            </td>
            <td
              v-for="resource in resources"
              :key="resource.key"
              class="permission-cell"
            >
              <div
                class="permission-dot"
                :class="[getPermissionClass(admin.permissions[resource.key])]"
                role="button"
                tabindex="0"
              ></div>
            </td>
            <td class="actions-cell">
              <div class="d-flex" style="gap: 10px">
                <feather-icon
                  icon="EyeIcon"
                  size="16"
                  class="align-middle text-primary cursor-pointer"
                  @click="handleShowDetailModal(admin)"
                />
                <feather-icon
                  icon="EditIcon"
                  size="16"
                  class="align-middle text-warning cursor-pointer"
                  @click="handleEditUser(admin)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="mx-2 mb-2">
      <b-row>
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-start"
        >
          <span class="text-muted">
            Showing {{ dataMeta.from }} to {{ dataMeta.to }} of
            {{ dataMeta.of }} admins
          </span>
        </b-col>
        <!-- Pagination -->
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-end"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="totalAdmins"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0 mt-1 mt-sm-0"
            prev-class="prev-item"
            next-class="next-item"
          >
            <template #prev-text>
              <feather-icon icon="ChevronLeftIcon" size="18" />
            </template>
            <template #next-text>
              <feather-icon icon="ChevronRightIcon" size="18" />
            </template>
          </b-pagination>
        </b-col>
      </b-row>
    </div>

    <!-- DETAIL MODAL -->
    <b-modal
      ref="modal"
      :title="title"
      centered
      cancel-variant="outline-secondary"
      hide-footer
    >
      <div
        v-if="detailItem"
        class="d-flex align-items-center p-1 my-1"
        style="color: #000; background: #f9fafb; border-radius: 12px"
      >
        <b-avatar variant="secondary" class="mr-2" style="background: gray">
          <feather-icon icon="UsersIcon" size="22" />
        </b-avatar>
        <div class="d-flex flex-column">
          <span class="font-weight-bold" style="font-size: 16px">{{
            `${detailItem.firstName}${
              detailItem.middleName ? ` ${detailItem.middleName} ` : " "
            }${detailItem.lastName}`
          }}</span>
          <span>{{ detailItem.email }}</span>
          <span v-if="detailItem.roles.length > 0"
            >Role: {{ detailItem.roles[0].name }}</span
          >
        </div>
      </div>
      <div v-if="permissions">
        <div v-for="group in permissionGroups" :key="group.title" class="mb-1">
          <h5
            class="mb-1"
            style="border-bottom: 1px solid #e0e0e0; padding-bottom: 5px"
          >
            {{ group.title }}
          </h5>
          <b-row>
            <b-col
              v-for="permission in group.permissions"
              :key="permission.key"
              cols="12"
              md="6"
              class="mb-1"
            >
              <div
                class="p-1"
                style="border: 1px solid #e0e0e0; border-radius: 5px"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <div class="">
                    <h6 class="m-0">{{ permission.title }}</h6>
                    <p
                      class="text-muted m-0"
                      style="
                        font-size: 12px;
                        line-height: 14px;
                        max-width: 120px;
                      "
                    >
                      {{ permission.description }}
                    </p>
                  </div>
                  <div
                    class="d-flex align-items-center justify-content-end"
                    style="gap: 5px; margin-left: 4px"
                  >
                    <div
                      class="permission-dot"
                      :class="[getPermissionClass(permission.value)]"
                    ></div>
                    <p
                      class="font-weight-bold text-center m-0"
                      style="
                        font-size: 12px;
                        line-height: 14px;
                        max-width: 40px;
                        color: black;
                      "
                    >
                      {{ getPermissionLabel(permission.value) }}
                    </p>
                  </div>
                </div>
              </div>
            </b-col>
          </b-row>
        </div>
      </div>
    </b-modal>

    <!-- ADD/UPDATE ADMIN -->
    <b-modal
      ref="user"
      :title="modalTitle"
      centered
      hide-footer
      cancel-variant="outline-secondary"
      @hide="hideModal"
    >
      <section style="color: black">
        <!-- Form: Information Form -->
        <validation-observer ref="simpleRules">
          <b-form class="mt-1">
            <b-row class="">
              <!-- Title -->
              <b-col cols="12">
                <b-form-group>
                  <div class="d-flex">
                    <label for="title">Title</label>
                  </div>

                  <validation-provider
                    #default="{ errors }"
                    name="title"
                    vid="title"
                    rules="required"
                  >
                    <b-form-radio-group
                      id="title"
                      v-model="dataUser.title"
                      :options="titleOptions"
                      value="Mr"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- First Name -->
              <b-col cols="12" md="6">
                <b-form-group>
                  <div class="d-flex">
                    <label for="first name">First Name</label>
                  </div>

                  <validation-provider
                    #default="{ errors }"
                    name="firstName"
                    vid="firstName"
                    rules="required"
                  >
                    <b-form-input v-model="dataUser.firstName" />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Last Name -->
              <b-col cols="12" md="6">
                <b-form-group>
                  <div class="d-flex">
                    <label for="last name">Last Name</label>
                  </div>
                  <validation-provider
                    #default="{ errors }"
                    name="lastName"
                    vid="lastName"
                    rules="required"
                  >
                    <b-form-input v-model="dataUser.lastName" />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Email -->
              <b-col cols="12" md="6">
                <b-form-group>
                  <div class="d-flex">
                    <label for="email">Email</label>
                  </div>
                  <validation-provider
                    #default="{ errors }"
                    name="email"
                    vid="email"
                    rules="required|email"
                  >
                    <b-form-input v-model="dataUser.email" />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Phone Number -->
              <b-col cols="12" md="6">
                <b-form-group>
                  <div class="d-flex">
                    <label for="phone number">Phone Number</label>
                  </div>
                  <validation-provider
                    #default="{ errors }"
                    name="phoneNumber"
                    vid="phoneNumber"
                    rules="required|phone"
                  >
                    <b-form-input
                      v-model="dataUser.phoneNumber"
                      type="tel"
                      maxlength="10"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Password -->
              <b-col cols="12" md="6">
                <b-form-group label="Password" label-for="password">
                  <validation-provider
                    #default="{ errors }"
                    name="Password"
                    vid="Password"
                    rules="required|min:6|password"
                  >
                    <b-input-group class="input-group-merge">
                      <b-form-input
                        v-model="dataUser.password"
                        :type="passwordTypeNew"
                        name="new-password"
                        placeholder="New Password"
                        autocomplete="new-password"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          :icon="passwordToggleIconNew"
                          class="cursor-pointer"
                          @click="togglePasswordNew"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Confirm password -->
              <b-col cols="12" md="6">
                <b-form-group
                  label="Confirm Password"
                  label-for="confirm-password"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Confirm Password"
                    rules="required|confirmed:Password"
                  >
                    <b-input-group class="input-group-merge">
                      <b-form-input
                        v-model="dataUser.confirmPassword"
                        :type="passwordConfirm"
                        name="confirm-password"
                        placeholder="Confirm Password"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          :icon="passwordToggleIconConfirm"
                          class="cursor-pointer"
                          @click="togglePasswordConfirm"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Birth Date -->
              <b-col cols="12" md="6">
                <b-form-group label="Birth Date" label-for="birth-date">
                  <validation-provider
                    #default="{ errors }"
                    rules="required"
                    name="dob"
                    vid="dob"
                  >
                    <flat-pickr
                      v-model="dataUser.dob"
                      class="form-control"
                      :config="configDate"
                      placeholder="DD-MM-YYYY"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

               <!-- USER Name -->
              <b-col cols="12" md="6">
                <b-form-group>
                  <div class="d-flex">
                    <label for="first name">Username</label>
                  </div>

                  <validation-provider
                    #default="{ errors }"
                    name="userName"
                    vid="userName"
                    rules="required"
                  >
                    <b-form-input v-model="dataUser.userName" />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Address -->
              <!-- <b-col>
                <b-form-group label="Address" label-for="address">
                  <validation-provider
                    #default="{ errors }"
                    rules="required"
                    name="fullAddress"
                    vid="fullAddress"
                  >
                    <vue-google-autocomplete
                      id="map"
                      ref="fullAddress"
                      v-model="fullAddress"
                      class="form-control"
                      placeholder="Full Address"
                      country="aus"
                      :fields="fields"
                      @placechanged="getAddressData"
                      @inputChange="changeInput"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col> -->

              <!-- Assign Role -->
              <b-col cols="12">
                <b-form-group>
                  <validation-provider
                    #default="{ errors }"
                    name="role"
                    vid="role"
                    rules="required"
                  >
                    <label>Assign Role</label>
                    <b-form-select
                      v-model="dataUser.role"
                      :options="optionsRole"
                    >
                      <template #first>
                        <b-form-select-option :value="null" disabled
                          >Select a role</b-form-select-option
                        >
                      </template>
                    </b-form-select>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Status -->
              <b-col cols="12" md="6">
                <label class="mb-1">Status</label>
                <b-form-radio-group
                  id="active-status"
                  v-model="dataUser.isActive"
                  :options="optionsAdminStatus"
                  name="active-status"
                />
              </b-col>
            </b-row>

            <b-row class="mt-1" style="border-top: 1px solid gray">
              <b-col class="d-flex justify-content-between mt-1">
                <b-button
                  variant="outline-secondary"
                  @click="$refs.user.hide()"
                >
                  Cancel
                </b-button>
                <template>
                  <b-button
                    v-if="modalTitle === 'Add New Admin User'"
                    variant="primary"
                    class="mb-1 mb-sm-0"
                    :block="$store.getters['app/currentBreakPoint'] === 'xs'"
                    :disabled="loadingCreateAcc"
                    @click="handleSubmitModal"
                  >
                    <Spinner v-if="loadingCreateAcc" />
                    <template v-else> Create Admin </template>
                  </b-button>
                  <b-button
                    v-else
                    variant="primary"
                    class="mb-1 mb-sm-0"
                    :block="$store.getters['app/currentBreakPoint'] === 'xs'"
                    :disabled="loadingUpdateProfile"
                    @click="handleSubmitModalUpdate"
                  >
                    <Spinner v-if="loadingUpdateProfile" />
                    <template v-else> Update </template>
                  </b-button>
                </template>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </section>
    </b-modal>
  </div>
</template>

<script>
import {
  BAvatar,
  BButton,
  BCard,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormRadioGroup,
  BFormSelect,
  BFormSelectOption,
  BInputGroup,
  BInputGroupAppend,
  BPagination,
  BPopover,
  BRow,
} from "bootstrap-vue";
import { mapActions, mapState } from "vuex";
import useToastHandler from "@/services/toastHandler";
import { permissionConfig, resources } from "@/@core/utils/enum/user";
import { computed, ref } from "@vue/composition-api";
import flatPickr from "vue-flatpickr-component";
import { calculateDate18YearsAgo } from "@core/utils/enum/date";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import VueGoogleAutocomplete from "vue-google-autocomplete";

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    BPopover,
    BButton,
    BCard,
    BCol,
    BRow,
    BPagination,
    BAvatar,
    BFormInput,
    BFormRadioGroup,
    BInputGroup,
    BInputGroupAppend,
    BFormGroup,
    BFormSelect,
    BForm,
    BFormSelectOption,
    flatPickr,
    VueGoogleAutocomplete,
  },
  props: {},
  data() {
    return {
      showPopup: false,
      popupTarget: "",
      currentRoleId: null,
      currentResourceKey: null,
      currentResource: "",
      selectedPermission: null,
      originalPermissions: {},
      permissionOptions: [
        { value: "none", label: "No Access" },
        { value: "read", label: "Read" },
        { value: "write", label: "Read / Write" },
        { value: "admin", label: "Admin" },
      ],
      changes: {},
      title: "",
      permissions: null,
      listRolePermission: [],
      detailItem: null,

      modalTitle: "",
      dataUser: {
        title: "Mr",
        firstName: "",
        lastName: "",
        email: "",
        role: null,
        phoneNumber: "",
        password: "",
        confirmPassword: "",
        isActive: true,
        dob: null,
        userName: "",
      },
      passwordTypeNew: "password",
      passwordConfirm: "password",
      optionsRole: [],
      optionsAdminStatus: [
        { text: "Active", value: true },
        { text: "Inactive", value: false },
      ],
      // fields: [
      //   "address_components",
      //   "adr_address",
      //   "alt_id",
      //   "formatted_address",
      //   "geometry",
      //   "id",
      //   "name",
      //   "scope",
      //   "type",
      //   "vicinity",
      // ],
      // fullAddress: "",
      // suburb: "",
      // state: "",
      // postCode: "",
      // country: "AUS",
      // streetName: "",
      // streetNumber: "",
      // streetType: "",
      titleOptions: [
        { text: "Mr", value: "Mr" },
        { text: "Ms", value: "Ms" },
        { text: "Mrs", value: "Mrs" },
        { text: "Miss", value: "Miss" },
        { text: "Dr", value: "Dr" },
      ],
      configDate: {
        altFormat: "d-m-Y",
        altInput: true,
        maxDate: this.calculateDate18YearsAgo(),
        static: true,
      },
    };
  },
  computed: {
    ...mapState("user/permission", ["dataListPermission"]),
    ...mapState("user/list", [
      "loadingUserList",
      "loadingUpdateProfile",
    ]),
    ...mapState("user/admin", ["loadingCreateAcc", "loadingDeleteAcc",   "loadingAdminList",
      "pagination",]),
    hasChanges() {
      return Object.keys(this.changes).length > 0;
    },
    totalChanges() {
      return Object.keys(this.changes).length;
    },
    permissionGroups() {
      return Object.keys(this.permissionConfig).map((groupKey) => {
        const group = this.permissionConfig[groupKey];
        return {
          title: group.title,
          permissions: Object.keys(group.permissions).map((permKey) => {
            const permConfig = group.permissions[permKey];
            const currentValue = this.permissions[permKey] || "none";

            return {
              key: permKey,
              title: permConfig.title,
              description: permConfig.description,
              value: currentValue,
              enabled: true,
              disabled: false,
            };
          }),
        };
      });
    },
    passwordToggleIconNew() {
      return this.passwordTypeNew === "password" ? "EyeIcon" : "EyeOffIcon";
    },
    passwordToggleIconConfirm() {
      return this.passwordConfirm === "password" ? "EyeIcon" : "EyeOffIcon";
    },
  },
  async mounted() {
    await this.fetchListRolePermission();
  },
  watch: {
    listRolePermission(val) {
      if (val.length > 0) this.fetchListUserPermission();
    },
    currentPage(val) {
      this.fetchListUserPermission();
    },
  },

  methods: {
    ...mapActions({
      getRoleList: "user/admin/getRoleList",
      createRole: "user/admin/createRole",
      createAccount: "user/admin/createAccount",
      setUserRole: "user/admin/setUserRole",
      updateUserProfile: "user/list/updateUserProfile",
      getTotalAdmin: "user/statistic/getTotalAdmin",
      createAdmin: "user/admin/createAdmin",
      getAdminList: "user/admin/getAdminList",
    }),
    closePermissionPopup() {
      this.showPopup = false;
      this.popupTarget = null;
      this.currentRoleId = null;
      this.currentResourceKey = null;
      this.selectedPermission = null;
      document.removeEventListener("click", this.handleClickOutside, true);
    },
    selectPermission(permission) {
      if (this.currentRoleId && this.currentResourceKey) {
        const role = this.adminList.find((u) => u.id === this.currentRoleId);

        if (!this.originalPermissions[this.currentRoleId]) {
          this.$set(this.originalPermissions, this.currentRoleId, {});
        }

        const originalValue =
          this.originalPermissions[this.currentRoleId][
            this.currentResourceKey
          ] !== undefined
            ? this.originalPermissions[this.currentRoleId][
                this.currentResourceKey
              ]
            : role.permissions[this.currentResourceKey];

        this.$set(role.permissions, this.currentResourceKey, permission);
        this.selectedPermission = permission;

        const changeKey = `${this.currentRoleId}-${this.currentResourceKey}`;
        if (permission !== originalValue) {
          this.$set(this.changes, changeKey, {
            roleId: this.currentRoleId,
            resourceKey: this.currentResourceKey,
            oldValue: originalValue,
            newValue: permission,
          });
        } else {
          this.$delete(this.changes, changeKey);
        }

        this.closePermissionPopup();
      }
    },
    getPermissionClass(permission) {
      const classes = {
        none: "permission-none",
        read: "permission-read",
        write: "permission-write",
        admin: "permission-admin",
      };
      return classes[permission] || "permission-none";
    },
    handleClickOutside(event) {
      if (!this.showPopup) return;

      const popoverElements = document.querySelectorAll(
        '.popover, .permission-popover, [class*="popover"]'
      );
      let clickedInsidePopover = false;

      popoverElements.forEach((popover) => {
        if (popover.contains(event.target)) {
          clickedInsidePopover = true;
        }
      });

      const clickedOnCurrentTarget =
        this.popupTarget &&
        (event.target === this.popupTarget ||
          this.popupTarget.contains(event.target));

      if (!clickedInsidePopover && !clickedOnCurrentTarget) {
        this.closePermissionPopup();
      }
    },
    getFilteredPermissions(userPermissions, permissionsList) {
      const result = [];

      for (const [key, value] of Object.entries(userPermissions)) {
        const actions =
          value === "admin" ? ["read", "write", "delete"] : [value];

        for (const action of actions) {
          if (action === "none") {
            continue;
          }

          const targetCode = `${key}_${action}`;

          const foundPermission = permissionsList.find(
            (item) => item.code === targetCode
          );

          if (foundPermission) {
            result.push(foundPermission);
          }
        }
      }

      return result;
    },
    getPermissionLabel(value) {
      switch (value) {
        case "admin":
          return "Admin";
        case "write":
          return "Read/ Write";
        case "read_only":
        case "read":
          return "Read Only";
        default:
          return "None";
      }
    },
    handleShowDetailModal(item) {
      this.title =
        "User Template Details" +
        " - " +
        `${item.firstName}${item.middleName ? ` ${item.middleName} ` : " "}${
          item.lastName
        }`;
      this.detailItem = item;
      this.$refs.modal.show();
      this.permissions = item.permissions || {};
    },
    async fetchListRolePermission() {
      const data = await this.getRoleList({
        page: 1,
        limit: 100,
        additionalDetail: "withPermissions",
      });
      if (!data) return this.showToastError(false, "Error fetching role list");

      this.optionsRole = data.data.map((item) => ({
        text: item.name,
        value: item.roleId,
      description: item.description,
      }));

      this.listRolePermission = data.data.map((i) => {
        const processedPermissions = this.processPermissions(i.permissions);

        this.$set(this.originalPermissions, i.code, {
          ...processedPermissions,
        });

        return {
          id: i.code,
          text: i.name,
          description: i.description,
          permissions: processedPermissions,
        };
      });
    },
    processPermissions(permissionsList) {
      const groupedPermissions = {};

      if (permissionsList.some((i) => i.code?.includes("full")))
        return this.resources.reduce((accumulator, currentResource) => {
          accumulator[currentResource.key] = "admin";
          return accumulator;
        }, {});

      permissionsList.forEach((p) => {
        const lastUnderscoreIndex = p.code?.lastIndexOf("_");
        const sectionKey =
          lastUnderscoreIndex !== -1
            ? p.code?.substring(0, lastUnderscoreIndex)
            : p.code;

        if (!groupedPermissions[sectionKey]) {
          groupedPermissions[sectionKey] = {
            read: false,
            write: false,
            delete: false,
          };
        }
        if (p.code?.includes("_read")) {
          groupedPermissions[sectionKey].read = true;
        } else if (p.code?.includes("_write")) {
          groupedPermissions[sectionKey].write = true;
        } else if (p.code?.includes("_delete")) {
          groupedPermissions[sectionKey].delete = true;
        }
      });
      const finalData = {};
      for (const section in groupedPermissions) {
        const { read, write, delete: del } = groupedPermissions[section];

        if (read && write && del) {
          finalData[section] = "admin";
        } else if (write) {
          finalData[section] = "write";
        } else if (read) {
          finalData[section] = "read";
        } else {
          finalData[section] = "none";
        }
      }

      const allSections = this.resources.map((resource) => resource.key);
      allSections.forEach((section) => {
        if (!finalData[section]) {
          finalData[section] = "none";
        }
      });
      return finalData;
    },
    async fetchListUserPermission() {
      const data = await this.getAdminList({
        isAdminUser: 1,
        page: this.currentPage,
        limit: this.perPage,
      });
      if (!data) return this.showToastError(false, "Error fetching role list");
      this.totalAdmins = this.pagination.total;

      this.adminList = data.map((admin) => {
        const roleItem = this.listRolePermission.find(
          (role) => role.id === admin.roles?.[0]?.code
        );
        if (roleItem)
          return {
            ...admin,
            permissions: roleItem.permissions,
          };
        return {
          ...admin,
          permissions: this.resources.reduce((accumulator, currentResource) => {
            accumulator[currentResource.key] = "none";
            return accumulator;
          }, {}),
        };
      });
    },
    handleShowModal() {
      this.modalTitle = "Add New Admin User";
      this.$refs.user.show();
    },
    togglePasswordNew() {
      this.passwordTypeNew =
        this.passwordTypeNew === "password" ? "text" : "password";
    },
    togglePasswordConfirm() {
      this.passwordConfirm =
        this.passwordConfirm === "password" ? "text" : "password";
    },
    async handleSubmitModalUpdate() {
      const valid = await this.$refs.simpleRules.validate();
      if (!valid) return;
      const data = {
        phoneNumber: `+61${this.dataUser.phoneNumber?.slice(1, 10)}`,
        firstName: this.dataUser.firstName,
        lastName: this.dataUser.lastName,
        dob: this.dataUser.dob,
        title: this.dataUser.title,
        gender: this.dataUser.title === "Mr" ? "M" : "F",
        email: this.dataUser.email,
        suburb: this.suburb,
        state: this.state,
        postCode: this.postCode,
        country: this.country,
        password: this.dataUser.password,
        streetName: this.streetName,
        streetNumber: this.streetNumber,
        streetType: this.streetType,
        fullAddress: this.fullAddress,
        status: this.dataUser.isActive ? 'active' : 'inactive',
        username: this.dataUser.userName,
      };
      const res = await this.updateUserProfile({
        userId: this.updateUser.userId,
        ...data,
      });
      if (this.updateUser.roles[0].roleId !== this.dataUser.role) {
        await this.setUserRole({
          userId: this.updateUser.userId,
          roleId: this.dataUser.role,
        });
      }
      if (res) {
        this.showToastSuccess(true, "Profile updated successfully!");
        this.fetchListUserPermission();
      }
      this.$refs.user.hide();
    },
    // getAddressData(addressData, place) {
    //   this.fullAddress = place.formatted_address;
    //   const routeItem = place.address_components.find((item) =>
    //     item.types.includes("route")
    //   );

    //   const streetLongName = routeItem?.long_name.split(" ");
    //   this.streetType = streetLongName?.pop() || "";
    //   this.streetName = streetLongName.join(" ") || "";
    //   const postCodeItem = place.address_components.find((item) =>
    //     item.types.includes("postal_code")
    //   );
    //   this.postCode = postCodeItem?.long_name || "";
    //   const streetNumberItem = place.address_components.find((item) =>
    //     item.types.includes("street_number")
    //   );
    //   this.streetNumber = streetNumberItem?.long_name || "";
    //   const subpremiseItem = place.address_components.find((item) =>
    //     item.types.includes("subpremise")
    //   );
    //   this.unitNumber = subpremiseItem?.long_name || "";
    //   const stateItem = place.address_components.find((item) =>
    //     item.types.includes("administrative_area_level_1")
    //   );
    //   this.state = stateItem?.long_name || "";
    //   const cityItem = place.address_components.find((item) =>
    //     item.types.includes("locality")
    //   );
    //   this.suburb = cityItem?.long_name || "";
    // },
    changeInput(e) {
      if (e.newVal === "") this.fullAddress = null;
    },
    hideModal() {
      this.dataUser = {
        title: "Mr",
        firstName: "",
        lastName: "",
        email: "",
        role: null,
        phoneNumber: "",
        password: "",
        confirmPassword: "",
        isActive: true,
            dob: null,
        userName: "",
      };
      this.fullAddress = null;
      this.passwordTypeNew = "password";
      this.passwordConfirm = "password";
      this.modalTitle = "";
    },
    handleEditUser(item) {
      this.updateUser = item;
      this.modalTitle = "Edit Admin User";
      this.dataUser = {
        title: item.title,
        firstName: item.firstName,
        lastName: item.lastName,
        email: item.email,
        role: item.roles[0].roleId,
        phoneNumber: item.phoneNumber?.replace("+61", "0") || null,
        password: "",
        confirmPassword: "",
        isActive: item.status === 'active',
        dob: item.dob,
        userName: item.username,
      };
      // this.fullAddress = item.fullAddress;
      // this.country = item?.country || null;
      // this.unitNumber = item?.unitNumber || "";
      // this.streetNumber = item?.streetNumber || "";
      // this.streetName = item?.streetName || "";
      // this.streetType = item?.streetType || "";
      // this.postCode = item?.postCode || "";
      // this.suburb = item?.suburb || "";
      // this.state = item?.state || "";
      this.$refs.user.show();
    },
    async handleSubmitModal() {
      const valid = await this.$refs.simpleRules.validate();
      if (!valid) return;
      const data = {
        phoneNumber: `+61${this.dataUser.phoneNumber?.slice(1, 10)}`,
        firstName: this.dataUser.firstName,
        lastName: this.dataUser.lastName,
        dob: this.dataUser.dob,
        title: this.dataUser.title,
        gender: this.dataUser.title === "Mr" ? "M" : "F",
        email: this.dataUser.email,
        suburb: this.suburb,
        state: this.state,
        postCode: this.postCode,
        country: this.country,
        password: this.dataUser.password,
        streetName: this.streetName,
        streetNumber: this.streetNumber,
        streetType: this.streetType,
        // fullAddress: this.fullAddress,
        status: this.dataUser.isActive ? 'active' : 'inactive',
                roleId: this.dataUser.role,
        username: this.dataUser.userName,
      };
      const res = await this.createAccount(data);
      if (res.data) {
        // await this.setUserRole({
        //   userId: res.data.userId,
        //   roleId: this.dataUser.role,
        // });
        this.showToastSuccess(true, "New admin user created successfully!");
        this.fetchListUserPermission();
      }
      this.$refs.user.hide();
      this.resetModal();
      this.getTotalAdmin();
    },
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside, true);
  },
  setup() {
    const totalAdmins = ref(0);
    const currentPage = ref(1);
    const perPage = ref(10);
    const adminList = ref([]);

    const dataMeta = computed(() => {
      const localItemsCount = adminList.value.length;
      return {
        from:
          perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
        to: perPage.value * (currentPage.value - 1) + localItemsCount,
        of: totalAdmins.value,
      };
    });

    const { showToastError, showToastSuccess } = useToastHandler();

    return {
      showToastError,
      showToastSuccess,
      resources,
      permissionConfig,

      adminList,
      totalAdmins,
      currentPage,
      perPage,
      dataMeta,
      calculateDate18YearsAgo,
    };
  },
};
</script>

<style scoped>
.permissions-matrix {
  background: #f8f9fa;
  min-height: 100vh;
}

.table {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.user-column {
  min-width: 200px;
  background: #f8f9fa;
  vertical-align: middle;
  text-align: center;
}

.resource-header {
  min-width: 100px;
  text-align: center;
  background: #f8f9fa;
  font-size: 11px;
  font-weight: 600;
  color: #6c757d;
  padding: 20px 8px;
  height: 120px;
  vertical-align: bottom;
}

.resource-title {
  transform: rotate(-45deg);
  transform-origin: center center;
  white-space: nowrap;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  line-height: 1;
}

.actions-column {
  min-width: 120px;
  background: #f8f9fa;
  vertical-align: middle;
  text-align: center;
}

.user-info {
  background: #f8f9fa;
}

.user-avatar {
  font-size: 24px;
}

.user-name {
  font-weight: 600;
  font-size: 14px;
}

.user-role {
  font-size: 12px;
  color: #6c757d;
}

.permission-cell {
  text-align: center;
  padding: 12px;
  background: white;
  position: relative;
}

.permission-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  margin: 0 auto;
  /* cursor: pointer; */
  transition: all 0.2s ease;
  position: relative;
}

.permission-none {
  background-color: #dee2e6;
}

.permission-read {
  background-color: #007bff;
}

.permission-write {
  background-color: #28a745;
}

.permission-admin {
  background-color: #fd7e14;
}

.text-none {
  background-color: #dee2e6;
}

.text-read {
  background-color: #007bff;
}

.text-read-write {
  background-color: #28a745;
}

.text-admin {
  background-color: #fd7e14;
}

.actions-cell {
  background: white;
}

.permission-popover {
  max-width: 250px;
}

.permission-options {
  min-width: 200px;
}

.permission-option {
  padding: 8px 12px;
  /* cursor: pointer; */
  border-radius: 4px;
  margin-bottom: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background-color 0.2s ease;
}

.permission-option:hover {
  background-color: #f8f9fa;
}

.permission-option.active {
  background-color: #e3f2fd;
  border: 1px solid #2196f3;
}

.permission-option:last-child {
  margin-bottom: 0;
}

.permission-changed {
  position: relative;
}

.permission-changed::before {
  content: "";
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  border: 2px solid #007bff;
  border-radius: 4px;
  pointer-events: none;
}

@media (max-width: 768px) {
  .resource-title {
    writing-mode: horizontal-tb;
    height: auto;
    padding: 4px;
  }

  .user-column {
    min-width: 150px;
  }

  .resource-header {
    min-width: 60px;
    font-size: 10px;
  }
}
</style>

<style lang="scss">
.modal-title {
  font-weight: 900 !important;
}
</style>